import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import {
  DEMO_USER_REGISTRY,
  DEMO_ACCOUNTS_ADMIN,
  SEED_ACCOUNT_REQUESTS,
  ADMIN_SWITCHER_ROLES,
  getDemoUserDisplayName,
  getDefaultRouteForRole,
} from "@/data/dojoOsOrgModel.js";
import {
  allowDemoAuthFallback,
  isBackendConfigured,
  isHybridDataMode,
} from "@/config/backendStaging049.js";
import {
  apiLogin,
  apiLogout,
  apiChangePassword,
  persistBackendAuth,
  clearBackendAuth,
  getStoredBackendUser,
  getBackendToken,
} from "@/api/karateBackendApi.js";
import { getAuthErrorMessage } from "@/api/backendAuthMessages.js";
import { getKarateUi } from "@/i18n/karateUiI18n.js";
import { useI18n } from "@/coquille-bridge";
import { syncProfileAvatarFromBackend, clearLocalAvatarCache } from "@/services/karateAvatarStorage.js";
import { karateR0Login, karateR0ChangePassword, karateR0Me } from "@/api/karateR0ApiClient.js";
import { ensureKarateR0Session, clearKarateR0Session } from "@/api/karateR0SessionBridge.js";
import { mapR0RolesToUiIds, isAllowedUiRole, resolveSwitcherRoles } from "@/utils/karateRoleAccess.js";
import { findMembershipCredential } from "@/membership/membershipStore.js";

/** @typedef {'guest'|'student'|'sensei'|'hanshi'|'admin'|'assistant'|'parent'} KarateRole */
/** @typedef {'hanshi'|'sensei'|'assistant'|'parent'|'student'} ActiveRoleUi */

const AUTH_KEY = "karate-phase1-auth";
const REQUESTS_KEY = "karate-phase1-account-requests";

export { DEMO_USER_REGISTRY as DEMO_USERS, DEMO_ACCOUNTS_ADMIN };

const AuthContext = createContext(null);

function mapBackendRole(role) {
  if (role === "hanshi") return "hanshi";
  return role;
}

function authFromR0User(user) {
  const roles = user.roles ?? [];
  const availableRoleIds = mapR0RolesToUiIds(roles);
  const hanshi = roles.find((r) => r.role === "HANSHI");
  const sensei = roles.find((r) => r.role === "SENSEI");
  const role = hanshi ? "hanshi" : sensei ? "sensei" : availableRoleIds[0] || "student";
  const person = user.person ?? {};
  const stored = loadAuth();
  const preferred =
    stored.username === user.email && isAllowedUiRole(stored.activeRoleUi, availableRoleIds)
      ? stored.activeRoleUi
      : null;
  return {
    role,
    name: `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim() || user.email,
    username: user.email,
    activeRoleUi: preferred || (role === "hanshi" ? "hanshi" : role),
    availableRoleIds,
    authSource: "r0",
    mustChangePassword: Boolean(user.mustChangePassword),
    personId: person.id || null,
    phone: person.phone || null,
  };
}

async function tryKarateR0Login(identifier, password) {
  const key = String(identifier || "").trim().toLowerCase();
  let email = key.includes("@") ? key : null;
  if (!email) {
    const alias = {
      admin: import.meta.env.VITE_KARATE_R0_HANSHI_EMAIL || "local-hanshi@example.test",
      hanshi: import.meta.env.VITE_KARATE_R0_HANSHI_EMAIL || "local-hanshi@example.test",
      sensei: import.meta.env.VITE_KARATE_R0_SENSEI_EMAIL || "local-sensei@example.test",
    };
    email = alias[key] ?? null;
  }
  if (!email) return null;
  const login = await karateR0Login(email, String(password || "").trim());
  if (!login.response.ok || !login.body?.user) return null;
  return authFromR0User(login.body.user);
}

function authFromBackendUser(user) {
  const role = mapBackendRole(user.role);
  return {
    role,
    name: user.displayName,
    username: user.legacyUsername || user.email,
    activeRoleUi: role === "hanshi" ? "hanshi" : role,
    backendUserId: user.id,
    studentId: user.studentId || null,
    childStudentIds: user.childStudentIds || [],
    dojoId: user.dojoIds?.[0] || null,
    instructorId: user.personId || null,
    authSource: "backend",
    mustChangePassword: Boolean(user.mustChangePassword),
    isStagingEmail: Boolean(user.isStagingEmail),
  };
}

function loadAuth() {
  if (typeof localStorage === "undefined") {
    return { role: "guest", name: "Visiteur", username: null, activeRoleUi: null, authSource: "local" };
  }
  const backendUser = getStoredBackendUser();
  const token = getBackendToken();
  if (backendUser && token && isHybridDataMode()) {
    return authFromBackendUser(backendUser);
  }
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return { role: "guest", name: "Visiteur", username: null, activeRoleUi: null, authSource: "local" };
    const parsed = JSON.parse(raw);
    return {
      role: parsed.role || "guest",
      name: parsed.name || "Visiteur",
      username: parsed.username || null,
      activeRoleUi: parsed.activeRoleUi || parsed.role || null,
      availableRoleIds: Array.isArray(parsed.availableRoleIds) ? parsed.availableRoleIds : undefined,
      authSource: parsed.authSource || "local",
      mustChangePassword: Boolean(parsed.mustChangePassword),
      personId: parsed.personId || null,
      phone: parsed.phone || null,
      membershipId: parsed.membershipId || null,
      studentId: parsed.studentId || null,
      dojoId: parsed.dojoId || null,
    };
  } catch {
    return { role: "guest", name: "Visiteur", username: null, activeRoleUi: null, authSource: "local" };
  }
}

function loadRequests() {
  if (typeof localStorage === "undefined") return [...SEED_ACCOUNT_REQUESTS];
  try {
    const raw = localStorage.getItem(REQUESTS_KEY);
    if (!raw) return [...SEED_ACCOUNT_REQUESTS];
    const parsed = JSON.parse(raw);
    return parsed.length ? parsed : [...SEED_ACCOUNT_REQUESTS];
  } catch {
    return [...SEED_ACCOUNT_REQUESTS];
  }
}

function persistRequests(requests) {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(REQUESTS_KEY, JSON.stringify(requests));
  }
}

export function AuthProvider({ children }) {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const [auth, setAuth] = useState(loadAuth);
  const [accountRequests, setAccountRequests] = useState(loadRequests);
  const [loginError, setLoginError] = useState(null);

  useEffect(() => {
    if (typeof localStorage !== "undefined" && !localStorage.getItem(REQUESTS_KEY)) {
      persistRequests(SEED_ACCOUNT_REQUESTS);
    }
  }, []);

  useEffect(() => {
    const onBackendAuth = () => setAuth(loadAuth());
    window.addEventListener("karate-backend-auth-updated", onBackendAuth);
    return () => window.removeEventListener("karate-backend-auth-updated", onBackendAuth);
  }, []);

  useEffect(() => {
    if (isHybridDataMode() && getBackendToken()) {
      syncProfileAvatarFromBackend().catch(() => {});
    }
  }, []);

  useEffect(() => {
    karateR0Me()
      .then(({ response, body }) => {
        if (!response.ok || !body?.user) return;
        const next = authFromR0User(body.user);
        setAuth(next);
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(AUTH_KEY, JSON.stringify(next));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (auth.role === "hanshi" || auth.role === "admin" || auth.role === "sensei") {
      ensureKarateR0Session({ auth }).catch(() => {});
    }
  }, [auth.role, auth.authSource, auth.username]);

  const persistAuth = useCallback((next) => {
    setAuth(next);
    if (next.authSource === "backend") return;
    if (next.role === "guest") {
      localStorage.removeItem(AUTH_KEY);
    } else {
      localStorage.setItem(AUTH_KEY, JSON.stringify(next));
    }
  }, []);

  const loginWithCredentials = useCallback(
    async (identifier, password) => {
      const key = String(identifier || "").trim().toLowerCase();

      if (isBackendConfigured() && isHybridDataMode()) {
        try {
          const result = await apiLogin(key, password, locale);
          persistBackendAuth({ token: result.token, user: result.user });
          const next = authFromBackendUser(result.user);
          setLoginError(null);
          setAuth(next);
          syncProfileAvatarFromBackend().catch(() => {});
          if (next.role === "hanshi" || next.role === "admin") {
            await ensureKarateR0Session({ auth: next, password });
          }
          return { ok: true, mustChangePassword: Boolean(result.user?.mustChangePassword || result.mustChangePassword) };
        } catch (err) {
          if (!allowDemoAuthFallback()) {
            setLoginError(getAuthErrorMessage(err, locale));
            return { ok: false };
          }
        }
      }

      const user = DEMO_USER_REGISTRY[key];
      const secret = String(password || "").trim();
      const demoPasswordOk =
        user && user.password.toLowerCase() === secret.toLowerCase();
      if (demoPasswordOk) {
        setLoginError(null);
        const defaultUi = user.role === "hanshi" ? "hanshi" : user.role;
        const next = {
          role: user.role,
          name: user.nameFr,
          username: user.username,
          activeRoleUi: defaultUi,
          availableRoleIds:
            user.role === "hanshi" || user.role === "admin"
              ? ADMIN_SWITCHER_ROLES.map((r) => r.id)
              : [defaultUi],
          authSource: "local",
          mustChangePassword: false,
        };
        persistAuth(next);
        if (next.role === "hanshi" || next.role === "admin") {
          await ensureKarateR0Session({ auth: next });
        }
        return { ok: true, mustChangePassword: false };
      }

      const membershipCred = findMembershipCredential(key, secret);
      if (membershipCred) {
        setLoginError(null);
        persistAuth({
          role: membershipCred.role,
          name: membershipCred.name,
          username: membershipCred.email,
          activeRoleUi: membershipCred.role,
          availableRoleIds: [membershipCred.role],
          authSource: "membership",
          membershipId: membershipCred.membershipId,
          studentId: membershipCred.studentId || null,
          dojoId: membershipCred.dojoId || null,
          mustChangePassword: false,
        });
        return { ok: true, mustChangePassword: false };
      }

      const r0Auth = await tryKarateR0Login(key, secret);
      if (r0Auth) {
        setLoginError(null);
        persistAuth(r0Auth);
        if (r0Auth.role === "hanshi" || r0Auth.role === "admin") {
          await ensureKarateR0Session({ auth: r0Auth, password: secret });
        }
        return { ok: true, mustChangePassword: r0Auth.mustChangePassword };
      }

      setLoginError(getKarateUi(locale).backendStaging.loginInvalidLocal);
      return { ok: false };
    },
    [locale, persistAuth],
  );

  const loginDemo = useCallback(
    (role, name) => {
      setLoginError(null);
      const next = {
        role,
        name,
        username: role,
        activeRoleUi: role === "hanshi" || role === "admin" ? "hanshi" : role,
        authSource: "local",
      };
      persistAuth(next);
      if (next.role === "hanshi" || next.role === "admin") {
        ensureKarateR0Session({ auth: next }).catch(() => {});
      }
    },
    [persistAuth],
  );

  const logout = useCallback(async () => {
    setLoginError(null);
    if (auth.authSource === "backend" || getBackendToken()) {
      await apiLogout();
    }
    await clearKarateR0Session().catch(() => {});
    clearBackendAuth();
    clearLocalAvatarCache();
    persistAuth({ role: "guest", name: "Visiteur", username: null, activeRoleUi: null, authSource: "local" });
  }, [auth.authSource, persistAuth]);

  const switchActiveRole = useCallback(
    (uiRole) => {
      const allowed =
        auth.availableRoleIds?.length > 0
          ? auth.availableRoleIds
          : auth.role === "hanshi" || auth.role === "admin"
            ? ADMIN_SWITCHER_ROLES.map((r) => r.id)
            : [auth.activeRoleUi || auth.role].filter(Boolean);
      if (!isAllowedUiRole(uiRole, allowed)) return;
      persistAuth({ ...auth, activeRoleUi: uiRole });
    },
    [auth, persistAuth],
  );

  const changeBackendPassword = useCallback(
    async ({ currentPassword, newPassword, confirmPassword }) => {
      const ui = getKarateUi(locale).authSecurity;
      if (auth.authSource === "r0") {
        const res = await karateR0ChangePassword({ currentPassword, newPassword, confirmPassword });
        if (!res.response.ok) {
          return {
            ok: false,
            message: res.body?.error || ui.weakPassword,
          };
        }
        const email = auth.username;
        if (!email) {
          persistAuth({ role: "guest", name: "Visiteur", username: null, activeRoleUi: null, authSource: "local" });
          return { ok: true, message: ui.success, requiresReLogin: true };
        }
        const login = await karateR0Login(email, newPassword);
        if (!login.response.ok || !login.body?.user) {
          persistAuth({ role: "guest", name: "Visiteur", username: null, activeRoleUi: null, authSource: "local" });
          return { ok: true, message: ui.success, requiresReLogin: true };
        }
        const next = authFromR0User(login.body.user);
        persistAuth(next);
        return { ok: true, message: ui.success };
      }
      try {
        const result = await apiChangePassword({
          currentPassword,
          newPassword,
          confirmPassword,
          locale,
        });
        persistBackendAuth({ token: result.token, user: result.user });
        const next = authFromBackendUser(result.user);
        setAuth(next);
        syncProfileAvatarFromBackend().catch(() => {});
        return {
          ok: true,
          message: locale === "en" ? result.messageEn : result.messageFr,
        };
      } catch (err) {
        return {
          ok: false,
          message: getAuthErrorMessage(err, locale) || ui.weakPassword,
        };
      }
    },
    [locale, auth.authSource, auth.username, persistAuth],
  );

  const submitAccountRequest = useCallback((payload) => {
    const entry = {
      id: `req-${Date.now()}`,
      ...payload,
      status: "a_valider",
      createdAt: new Date().toISOString(),
      demo: true,
    };
    setAccountRequests((prev) => {
      const next = [entry, ...prev];
      persistRequests(next);
      return next;
    });
    return entry;
  }, []);

  const updateAccountRequestStatus = useCallback((requestId, status) => {
    setAccountRequests((prev) => {
      const next = prev.map((r) => (r.id === requestId ? { ...r, status } : r));
      persistRequests(next);
      return next;
    });
  }, []);

  const activeRoleUi = auth.activeRoleUi || auth.role;
  const shellRole =
    activeRoleUi === "admin" ? "hanshi" : activeRoleUi === "guest" ? "guest" : activeRoleUi;

  const availableRoleIds = useMemo(() => {
    if (auth.availableRoleIds?.length) return auth.availableRoleIds;
    if (auth.role === "hanshi" || auth.role === "admin") return ADMIN_SWITCHER_ROLES.map((r) => r.id);
    const single = auth.activeRoleUi || auth.role;
    return single && single !== "guest" ? [single === "admin" ? "hanshi" : single] : [];
  }, [auth.availableRoleIds, auth.role, auth.activeRoleUi]);

  const value = useMemo(
    () => ({
      ...auth,
      activeRoleUi: shellRole,
      availableRoleIds,
      isAuthenticated: auth.role !== "guest",
      isAdmin: auth.role === "admin" || auth.role === "hanshi",
      canSwitchRole: availableRoleIds.length > 1,
      loginError,
      mustChangePassword: Boolean(auth.mustChangePassword),
      loginWithCredentials,
      loginDemo,
      logout,
      switchActiveRole,
      changeBackendPassword,
      submitAccountRequest,
      updateAccountRequestStatus,
      accountRequests,
      demoAccounts: DEMO_ACCOUNTS_ADMIN,
      demoRoles: resolveSwitcherRoles(availableRoleIds, locale),
      getDemoUserDisplayName,
      getDefaultRouteForRole,
      authSource: auth.authSource || "local",
      isBackendAuth: auth.authSource === "backend",
      isR0Auth: auth.authSource === "r0",
      canChangePassword: auth.authSource === "backend" || auth.authSource === "r0",
    }),
    [
      auth,
      shellRole,
      availableRoleIds,
      locale,
      loginError,
      loginWithCredentials,
      loginDemo,
      logout,
      switchActiveRole,
      changeBackendPassword,
      submitAccountRequest,
      updateAccountRequestStatus,
      accountRequests,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth requires AuthProvider");
  return ctx;
}

/** @param {KarateRole} role */
export function canViewDocument(visibilite, role) {
  if (visibilite === "public") return true;
  if (visibilite === "a_valider") return role === "admin" || role === "hanshi";
  if (visibilite === "membres") return role !== "guest";
  if (visibilite === "admin") return role === "admin" || role === "hanshi";
  return false;
}
